import { useTranslation } from "@i18n";
import { convertDate } from "../../../../../services/luxonHelper";
import { untranslated } from "@Helpers/types/i18n";
import ToolTipComponent from "../../../../../cubeComponent/html/tooltip";
import UserNameTagComponent from "../../../../../cubeComponent/html/UserNameTagComponent";
import type { ScheduleDetailRecord } from "@ServerTypes/tasks/tasks.schema";
import Text from "@CubeComponents/html/Text/Text";
import CopyToClipboard from "@Actions/CopyToClipboard";

interface ScheduleDetailsContentProps {
  scheduleData: ScheduleDetailRecord;
}

function ScheduleDetailsContent(props: Readonly<ScheduleDetailsContentProps>) {
  const { t } = useTranslation();
  const { scheduleData } = props;

  const addedByTemplate = () => {
    const _id = scheduleData.latestScheduleVersion?.updatedBy?._id;
    return <UserNameTagComponent id={_id} size="XS" chipBackground="#6BBBE340" />;
  };

  return (
    <div className="mb-[1.875rem] ms-2.5 flex flex-col gap-[1.875rem] overflow-hidden">
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Description")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          <Text>{scheduleData.description}</Text>
        </div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Link")}</Text>
        </div>
        <div className="flex-[2] flex items-center gap-1 overflow-hidden text-sm w-20">
          <Text className="overflow-hidden text-ellipsis whitespace-nowrap">{`${window.location.origin}/project/${scheduleData.projectId}/schedule/${scheduleData._id}/scheduleDetails?selectedTab=Gantt&projectId=${scheduleData.projectId}`}</Text>
          <span className="cursor-pointer shrink-0">
            <CopyToClipboard
              text={`${window.location.origin}/project/${scheduleData.projectId}/schedule/${scheduleData._id}/scheduleDetails?selectedTab=Gantt&projectId=${scheduleData.projectId}`}
            ></CopyToClipboard>
          </span>
        </div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Location")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          <Text>{scheduleData.description}</Text>
        </div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Start Date")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          <div style={{ width: "fit-content" }}>
            <ToolTipComponent
              showTipPointer={false}
              position="top"
              content={
                scheduleData.startDate
                  ? convertDate(Number(scheduleData.startDate), "dateForTooltip")
                  : untranslated("-")
              }
            >
              <Text>{scheduleData.startDate ? convertDate(Number(scheduleData.startDate), "dateForText") : "-"}</Text>
            </ToolTipComponent>
          </div>
        </div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("End Date")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          <div style={{ width: "fit-content" }}>
            <ToolTipComponent
              showTipPointer={false}
              position="top"
              content={
                scheduleData.endDate ? convertDate(Number(scheduleData.endDate), "dateForTooltip") : untranslated("-")
              }
            >
              <Text>{scheduleData.endDate ? convertDate(Number(scheduleData.endDate), "dateForText") : "-"}</Text>
            </ToolTipComponent>
          </div>
        </div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Duration")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          <Text>{scheduleData.duration ? `${scheduleData.duration} days` : "-"}</Text>
        </div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Default Calendar")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          <Text>{scheduleData.worktimes?.find((w) => w.isDefault)?.name ?? "-"}</Text>
        </div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Modified by")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">{addedByTemplate()}</div>
      </div>
      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Modified On")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          <div style={{ width: "fit-content" }}>
            <ToolTipComponent
              showTipPointer={false}
              position="top"
              content={
                scheduleData.createdDate
                  ? convertDate(Number(scheduleData.createdDate), "dateForTooltip")
                  : untranslated("-")
              }
            >
              <Text>
                {scheduleData.createdDate ? convertDate(Number(scheduleData.createdDate), "dateForText") : "-"}
              </Text>
            </ToolTipComponent>
          </div>
        </div>
      </div>

      <div className="flex justify-between ps-1">
        <div className="flex-[2] text-sm text-primary_text font-semibold">
          <Text>{t("Space")}</Text>
        </div>
        <div className="flex-[2] overflow-hidden text-ellipsis whitespace-nowrap text-sm">
          {scheduleData.space?.spaceName}
        </div>
      </div>
    </div>
  );
}

export default ScheduleDetailsContent;
